import type { SerializedBoardDto } from '../api/puzzles';

export interface ShareCardInput {
  date: string;
  difficulty: string;
  won: boolean;
  score: number;
  target: number;
  livesUsed: number;
  streak?: number | null;
  /** Final board — rendered as an emoji grid of the played area. */
  board?: SerializedBoardDto;
}

/**
 * Emoji snapshot of the played area (bounding box of placed tiles):
 * 🟩 = tile, ⬜ = empty square. Null when the board is empty.
 */
export function boardEmojiGrid(board: SerializedBoardDto): string | null {
  if (board.placed.length === 0) return null;
  const rows = board.placed.map((p) => p.row);
  const cols = board.placed.map((p) => p.col);
  const minRow = Math.min(...rows);
  const maxRow = Math.max(...rows);
  const minCol = Math.min(...cols);
  const maxCol = Math.max(...cols);
  const occupied = new Set(board.placed.map((p) => `${p.row},${p.col}`));

  const lines: string[] = [];
  for (let r = minRow; r <= maxRow; r++) {
    let line = '';
    for (let c = minCol; c <= maxCol; c++) {
      line += occupied.has(`${r},${c}`) ? '🟩' : '⬜';
    }
    lines.push(line);
  }
  return lines.join('\n');
}

/** Share-card text (spec 8.6, extended with the board snapshot + challenge):
 *
 *   Scrabble Daily 2026-06-10 (Medium) · ✓ 63/60 · 2 lives used · streak 12
 *   🟩🟩🟩⬜
 *   ⬜⬜🟩🟩
 *   Can you beat 63? Play today’s board → https://scrabbledaily.com
 *
 * The closing line is the viral hook: the sharer's score as a direct challenge
 * plus a full URL so chat apps unfurl a link preview (og-image + description).
 *
 * Deliberately NOT the words played: every player gets the same daily board,
 * so listing the words would hand the answers to anyone who hasn't played yet.
 * The emoji grid shows only the shape (like Wordle), which isn't a spoiler.
 */
export function buildShareText(input: ShareCardInput): string {
  const difficulty =
    input.difficulty.charAt(0).toUpperCase() + input.difficulty.slice(1).toLowerCase();
  const headerParts = [
    `Scrabble Daily ${input.date} (${difficulty})`,
    `${input.won ? '✓' : '✗'} ${input.score}/${input.target}`,
    `${input.livesUsed} ${input.livesUsed === 1 ? 'life' : 'lives'} used`,
  ];
  if (input.won && input.streak != null && input.streak > 0) {
    headerParts.push(`streak ${input.streak}`);
  }

  const lines = [headerParts.join(' · ')];
  const grid = input.board ? boardEmojiGrid(input.board) : null;
  if (grid) lines.push(grid);
  lines.push(`Can you beat ${input.score}? Play today’s board → https://scrabbledaily.com`);
  return lines.join('\n');
}

/** Web Share API on mobile when available, clipboard otherwise (spec 10.3). */
export async function shareResult(text: string): Promise<'shared' | 'copied'> {
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ text });
      return 'shared';
    } catch {
      // fall through to clipboard (user dismissed or share failed)
    }
  }
  await navigator.clipboard.writeText(text);
  return 'copied';
}
